import { Package, Product, ProductPriceEdit } from '@app/core/models';
import { HeaderSelectControlType } from '../enums/select-control-type.enum';

export function calculatePriceByMargin(productList: ProductPriceEdit[], priceMargin: number): ProductPriceEdit[] {
  return productList.map((product) =>
    product.updateByMargin
      ? {
          ...product,
          price: Math.min(99999999999999.99, Math.round(product.pack.productPrice.referencePrice * (1 + priceMargin / 100) * 100) / 100), // If the calculated price exceeds 99999999999999.99, it is capped at 99999999999999.99.
        }
      : product,
  );
}

export const filterPacksForAdding = (
  productsOrSelected: Product[] | ProductPriceEdit[],
  priceListToUpdate: ProductPriceEdit[],
  fromCategory: boolean = true,
): ProductPriceEdit[] => {
  return fromCategory
    ? (productsOrSelected as Product[]).flatMap((product) =>
        product.packages
          .filter(
            (pack) => pack.productPrice?.referencePrice > 0 && !priceListToUpdate.some((priceEdit) => priceEdit.pack.sCode === pack.sCode),
          )
          .map((pack) => ({
            productId: product.id,
            pack,
            price: pack.productPrice?.referencePrice,
            productType: product.productType,
            updateByMargin: true,
          })),
      )
    : (productsOrSelected as ProductPriceEdit[])
        .filter((product) => product.pack.productPrice.referencePrice > 0)
        .filter((product) => !priceListToUpdate.some((priceEdit) => priceEdit.pack.ean === product.pack.ean))
        .map((product) => ({ ...product, updateByMargin: true }));
};

export const filterPacksForRemoving = (
  productsOrSelected: Product[] | ProductPriceEdit[],
  priceListToUpdate: ProductPriceEdit[],
  fromCategory: boolean = true,
): { productId: string; packEan: string }[] => {
  return fromCategory
    ? (productsOrSelected as Product[]).flatMap((product) =>
        product.packages
          .filter((pack) => priceListToUpdate.some((priceEdit) => priceEdit.pack.sCode === pack.sCode && priceEdit.updateByMargin))
          .map((pack) => ({ productId: product.id, packEan: pack.ean })),
      )
    : (productsOrSelected as ProductPriceEdit[])
        .filter((product) => priceListToUpdate.some((priceEdit) => priceEdit.pack.ean === product.pack.ean && priceEdit.updateByMargin))
        .map((product) => ({ productId: product.productId, packEan: product.pack.ean }));
};

const isPackSelected = (pack: Package, priceListToUpdate: ProductPriceEdit[]) =>
  priceListToUpdate.some((pricePack) => pricePack.pack.ean === pack.ean && pricePack.updateByMargin);

const isProductSelected = (product: Product, priceListToUpdate: ProductPriceEdit[]) =>
  product.packages.some((pack) => isPackSelected(pack, priceListToUpdate));

const isEntireProductSelected = (product: Product, priceListToUpdate: ProductPriceEdit[]) =>
  product.packages.every((pack) => isPackSelected(pack, priceListToUpdate));

const hasAnyPackUpdateByMarginFalse = (product: Product, priceListToUpdate: ProductPriceEdit[]) =>
  product.packages.some((pack) => !priceListToUpdate.find((pricePack) => pricePack.pack.ean === pack.ean)?.updateByMargin);

const computeControlState = (
  products: Product[],
  priceListToUpdate: ProductPriceEdit[],
): { allSelected: boolean; indeterminate: boolean } => {
  const allInPriceList = products.every((product) => isEntireProductSelected(product, priceListToUpdate));
  const anyUpdateByMarginFalse = products.some((product) => hasAnyPackUpdateByMarginFalse(product, priceListToUpdate));

  return {
    allSelected: allInPriceList && !anyUpdateByMarginFalse,
    indeterminate: !allInPriceList && products.some((product) => isProductSelected(product, priceListToUpdate)),
  };
};

export const computeHeaderSelectControlState = (
  priceListToUpdate: ProductPriceEdit[],
  products: Product[],
  selectControlType: HeaderSelectControlType,
): Record<string, { allSelected: boolean; indeterminate: boolean }> => {
  return {
    [selectControlType]: computeControlState(products, priceListToUpdate),
  };
};

export const computeCategorySelectControlStates = (
  priceListToUpdate: ProductPriceEdit[],
  filteredProducts: Product,
): Record<string, { allSelected: boolean; indeterminate: boolean; selectedCount: number }> => {
  const result: Record<string, { allSelected: boolean; indeterminate: boolean; selectedCount: number }> = {};

  Object.entries(filteredProducts).forEach(([categoryCode, products]) => {
    const selectedCount = products.filter((product) => isProductSelected(product, priceListToUpdate)).length;
    const { allSelected, indeterminate } = computeControlState(products, priceListToUpdate);

    result[categoryCode] = { allSelected, indeterminate, selectedCount };
  });
  return result;
};

export const computeProductSelectControlState = (
  prices: ProductPriceEdit[],
  priceListToUpdate: ProductPriceEdit[],
): { allSelected: boolean; isIndeterminate: boolean } => {
  const allSelected = prices.every((price) =>
    priceListToUpdate.some(
      (product) => product.productId === price.productId && product.pack.ean === price.pack.ean && product.updateByMargin,
    ),
  );

  const someSelected = prices.some((price) =>
    priceListToUpdate.some(
      (product) => product.productId === price.productId && product.pack.ean === price.pack.ean && product.updateByMargin,
    ),
  );

  return {
    allSelected,
    isIndeterminate: !allSelected && someSelected,
  };
};
